<template>
    <div class="workflow-status-panel">
        <div v-if="currentRun" class="pa-3">
            <!-- Status Section -->
            <div class="mb-4">
                <div class="d-flex align-center mb-3">
                    <v-chip :color="getStatusColor(currentRun.status)" label size="small" class="mr-2">
                        {{ currentRun.status.toUpperCase() }}
                    </v-chip>
                    <span class="text-subtitle-2 font-weight-medium text-truncate">{{ workflowName }}</span>
                </div>

                <v-list density="compact" class="bg-transparent pa-0">
                    <v-list-item class="px-0">
                        <template #prepend><v-icon size="small" color="grey">mdi-identifier</v-icon></template>
                        <v-list-item-title class="text-caption">{{ t('workflowEditor.runStatus.taskId') }}</v-list-item-title>
                        <v-list-item-subtitle class="text-caption font-monospace">{{ currentRun.taskId.slice(0, 8) }}...</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item class="px-0">
                        <template #prepend><v-icon size="small" color="grey">mdi-clock-start</v-icon></template>
                        <v-list-item-title class="text-caption">{{ t('workflowEditor.runStatus.started') }}</v-list-item-title>
                        <v-list-item-subtitle class="text-caption">{{ formatDateTime(currentRun.startedAt) }}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item class="px-0">
                        <template #prepend><v-icon size="small" color="grey">mdi-format-list-numbered</v-icon></template>
                        <v-list-item-title class="text-caption">{{ t('workflowEditor.runStatus.events') }}</v-list-item-title>
                        <v-list-item-subtitle class="text-caption">{{ eventCount }}</v-list-item-subtitle>
                    </v-list-item>
                </v-list>
            </div>

            <v-divider class="my-3" />

            <!-- Costs Section -->
            <div v-if="currentRun.costs">
                <div class="d-flex align-center mb-3">
                    <v-icon size="small" color="success" class="mr-2">mdi-currency-eur</v-icon>
                    <span class="text-subtitle-2">{{ t('workflowEditor.costs.totalCost') }}</span>
                    <v-spacer />
                    <span class="text-h6 font-weight-bold text-success">{{ formatCost(currentRun.costs.total_cost_eur) }}</span>
                </div>

                <template v-if="currentRun.costs.summary">
                    <!-- By Provider -->
                    <div class="mb-3">
                        <div class="text-caption text-grey mb-1">
                            <v-icon size="x-small" class="mr-1">mdi-cloud</v-icon>
                            {{ t('workflowEditor.costs.byProvider') }}
                        </div>
                        <v-list density="compact" class="bg-grey-lighten-4 rounded pa-0">
                            <v-list-item
                                v-for="(data, provider) in currentRun.costs.summary.by_provider"
                                :key="provider"
                                class="px-2 py-1"
                            >
                                <template #prepend>
                                    <v-icon size="x-small" color="primary">mdi-server</v-icon>
                                </template>
                                <v-list-item-title class="text-caption">{{ provider }}</v-list-item-title>
                                <template #append>
                                    <span class="text-caption font-weight-medium">{{ formatCost(data.cost) }}</span>
                                </template>
                            </v-list-item>
                        </v-list>
                    </div>

                    <!-- Models Used -->
                    <div class="mb-3">
                        <div class="text-caption text-grey mb-1">
                            <v-icon size="x-small" class="mr-1">mdi-brain</v-icon>
                            {{ t('workflowEditor.costs.modelsUsed') }}
                        </div>
                        <div class="d-flex flex-wrap gap-1">
                            <template v-for="(data, provider) in currentRun.costs.summary.by_provider" :key="provider">
                                <v-chip
                                    v-for="model in data.models"
                                    :key="`${provider}-${model}`"
                                    size="x-small"
                                    variant="tonal"
                                    color="secondary"
                                >
                                    {{ model }}
                                </v-chip>
                            </template>
                        </div>
                    </div>

                    <!-- Statistics -->
                    <div>
                        <div class="text-caption text-grey mb-1">
                            <v-icon size="x-small" class="mr-1">mdi-chart-bar</v-icon>
                            {{ t('workflowEditor.costs.statistics') }}
                        </div>
                        <v-list density="compact" class="bg-grey-lighten-4 rounded pa-0">
                            <v-list-item class="px-2 py-1">
                                <v-list-item-title class="text-caption">{{ t('workflowEditor.costs.apiCalls') }}</v-list-item-title>
                                <template #append>
                                    <span class="text-caption font-weight-medium">{{ currentRun.costs.summary.entries }}</span>
                                </template>
                            </v-list-item>
                            <v-list-item v-if="currentRun.costs.summary.period" class="px-2 py-1">
                                <v-list-item-title class="text-caption">{{ t('workflowEditor.costs.duration') }}</v-list-item-title>
                                <template #append>
                                    <span class="text-caption">{{ formatCostPeriod(currentRun.costs.summary.period) }}</span>
                                </template>
                            </v-list-item>
                        </v-list>
                    </div>
                </template>
            </div>
            <div v-else class="text-center text-grey py-4">
                <v-icon size="32" color="grey-lighten-1">mdi-currency-eur-off</v-icon>
                <p class="text-caption mt-2">{{ t('workflowEditor.costs.noCostsYet') }}</p>
            </div>
        </div>
        <div v-else class="text-center text-grey pa-4">
            <v-icon size="48" color="grey-lighten-1">mdi-play-circle-outline</v-icon>
            <p class="mt-2">{{ t('workflowEditor.sidebar.noRunYet') }}</p>
        </div>
    </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import type { WorkflowRun } from '@/stores/WorkflowStore/WorkflowStore'
import { useCurrency } from '@/composables/useCurrency'

const props = defineProps<{
    currentRun: WorkflowRun | null
    workflowName: string
    eventCount: number
}>()

const { t } = useI18n()
const { formatEur, loadRate } = useCurrency()

onMounted(loadRate)

function getStatusColor(status: string): string {
    switch (status) {
        case 'completed': return 'success'
        case 'running': return 'primary'
        case 'pending': return 'warning'
        case 'error': return 'error'
        default: return 'grey'
    }
}

function formatDateTime(date: Date): string {
    if (!date) return ''
    return new Date(date).toLocaleString()
}

function formatCost(cost: number | undefined): string {
    return formatEur(cost)
}

function formatCostPeriod(period: { start?: string; end?: string } | undefined): string {
    if (!period?.start || !period?.end) return ''
    const start = new Date(period.start)
    const end = new Date(period.end)
    const diffMs = end.getTime() - start.getTime()
    const diffSec = Math.floor(diffMs / 1000)
    if (diffSec < 60) return `${diffSec}s`
    const diffMin = Math.floor(diffSec / 60)
    const remainingSec = diffSec % 60
    return `${diffMin}m ${remainingSec}s`
}
</script>

<style scoped>
.workflow-status-panel {
    height: 100%;
    overflow-y: auto;
}
</style>
